<script setup lang="ts">
import { i18n } from '@/i18n';
import { BulkRegisterResult } from '../domains/entities/UnmatchedOrderItem';
import { computed, ref } from 'vue';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { TABLE_HEADERS } from '../constants';
import { useUnmatchedOrderStore } from '../pages/useUnmatchedOrderStore';
import { storeToRefs } from 'pinia';
import EzTable from '@/corePagesModule/components/ezTable/EzTable.vue';
import EzTableRow from '@/corePagesModule/components/ezTable/EzTableRow.vue';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import OrderStatusBadge from '@/corePagesModule/components/OrderStatusBadge.vue';
import Mdi from '@/corePagesModule/components/Mdi.vue';

const t = i18n.t;

const store = useUnmatchedOrderStore();
const { state } = storeToRefs(store);
const props = withDefaults(defineProps<{ result: BulkRegisterResult }>(), {});

const isDisplayDetailTable = ref(false);

const displayHeaders = computed(() => [
  { key: 'rejectReason', label: '除外理由', width: '400px', type: 'uni' },
  ...TABLE_HEADERS.slice(1, -2),
]);
const displayItems = computed(() =>
  state.value.items.filter((i) =>
    Object.entries(props.result.rejectedUnmatchedOrders)
      .flatMap(([_, v]) => v)
      .includes(i.unmatchedOrderId)
  )
);
const registerRejectedCount = computed(() =>
  Object.entries(props.result.rejectedUnmatchedOrders).reduce((acc, [_, cur]) => (acc += cur.length), 0)
);

/**
 * IDから失敗理由を取得する
 * @param targetId - 調べるunmatchedOrderId
 */
const getRejectReason = (targetId: string) => {
  const res = Object.entries(props.result.rejectedUnmatchedOrders).find(([_, arr]) => arr?.includes(targetId))?.[0];
  return res ? t(res) : '--';
};
</script>
<template>
  <div class="bulk-register-result-modal-content">
    <div class="counts">
      <div class="counts__item">
        <span class="counts__item__label">{{ t('登録済:') }}</span>
        <span class="counts__item__value">{{ props.result.registeredCount }}</span>
        <span class="counts__item__label">{{ t('件') }}</span>
      </div>

      <div class="counts__item">
        <span class="counts__item__label">{{ t('除外:') }}</span>
        <span class="counts__item__value">{{ registerRejectedCount }}</span>
        <span class="counts__item__label">{{ t('件') }}</span>
      </div>
    </div>
    <div class="table__container">
      <button class="table__label" @click="isDisplayDetailTable = !isDisplayDetailTable">
        {{ t('除外した未照合発注の詳細') }}
        <Mdi icon="expand_more" />
      </button>
      <EzTable
        v-show="isDisplayDetailTable"
        :grid-template-columns="displayHeaders.map((h) => h.width)"
        sticky-header
        max-height="calc(100vh - 600px)"
      >
        <template #header>
          <EzHeader v-for="header in displayHeaders" :key="`header:${header.key}`">
            {{ t(header.label) }}
          </EzHeader>
        </template>
        <template #body>
          <EzTableRow
            v-for="(item, index) in displayItems"
            :key="item.unmatchedOrderId"
            :grid-template-columns="displayHeaders.map((h) => h.width)"
            class="unmatched-row"
          >
            <template v-for="header in displayHeaders" :key="`row:${header.key}:${index}`">
              <EzCell v-if="header.key === 'rejectReason'">
                <span class="error-reason">{{ getRejectReason(item.unmatchedOrderId) }}</span>
              </EzCell>
              <EzCell v-else-if="header.key === 'orderStatus'" force-center>
                <span class="order-status">
                  <OrderStatusBadge :order-status-str="item.orderStatus" />
                </span>
              </EzCell>
              <EzCell v-else-if="header.type === 'num'" is-number>
                {{ item[header.key]?.toLocaleString() ?? '' }}
              </EzCell>
              <EzCell v-else-if="header.type === 'date'" is-number>
                {{ item[header.key] ? DateService.convertMSUnixTimeToYYYYMMDDSlash(item[header.key]) : '' }}
              </EzCell>
              <EzCell v-else-if="header.type === 'str'" is-number>
                {{ item[header.key] ?? '' }}
              </EzCell>
            </template>
          </EzTableRow>
        </template>
      </EzTable>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.bulk-register-result-modal-content {
  display: grid;
  gap: 16px;
  overflow: hidden;
  *:not(.material-icons) {
    font-family: 'Noto Sans JP';
  }
}
.counts {
  width: 50%;
  margin: auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  justify-items: center;
  &__item {
    display: grid;
    grid-template-columns: repeat(3, max-content);
    gap: 4px;
    line-height: 1;
    align-items: end;
    &__label {
      font-size: 18px;
    }
    &__value {
      font-size: 36px;
      font-weight: 700;
    }
  }
}

.table {
  &__container {
    display: grid;
    gap: 8px;
    padding: 8px;
    border: 1px solid $borderDefault;
    border-radius: 8px;
  }
  &__label {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, auto);
    justify-content: space-between;
    padding: 8px;
    font-size: 16px;
  }
}

:deep(.ez-table .table__header-container) {
  width: 100%;
}

:deep(.table__header) {
  min-height: 36px;
}

:deep(.table__header__content) {
  line-height: 36px;
}

:deep(.table__cell) {
  border-bottom: 1px solid $surfaceSecondary;
}

:deep(.table__cell__content) {
  padding-top: 4px;
  padding-bottom: 4px;
}

.error-reason {
  color: $semanticError;
  font-family: Noto Sans JP;
  font-size: 12px;
  font-weight: 500;
  line-height: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
